'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { authService } from '@/services/http/authService'
import { isPublicPage } from '@/utils/routes/isPublicPage'

/**
 * Avisa o cronômetro quando a pessoa sai da aba, para o tempo da última página
 * ser creditado com a duração real (AC-086).
 *
 * **Só `pagehide`, e isso é decisão de produto.** Trocar de aba **não** para o
 * cronômetro: quem deixa a plataforma aberta e vai ao e-mail continua contando,
 * exatamente como conta hoje no caminho legado. Escutar `visibilitychange`
 * fecharia a página ao esconder a aba e, pior, o tempo de leitura entre voltar
 * para a aba e a navegação seguinte também sumiria — voltar para uma aba não
 * dispara navegação, então não haveria página aberta para creditar.
 *
 * `beforeunload` está fora por outro motivo: não dispara de forma confiável em
 * mobile e desliga o bfcache.
 *
 * **O que sobra descoberto.** No celular, o sistema pode matar a aba em segundo
 * plano sem emitir `pagehide`. Nesse caso a página fica aberta e quem a fecha é
 * a varredura de expiração, creditando `min(decorrido, MAX_PAGE_SECONDS)` — que
 * é justamente o teto para o caso de não ter havido sinal nenhum.
 *
 * **Por que não dispara duas vezes.** A trava espelha o estado do servidor: ela
 * responde "existe página aberta que eu ainda não mandei fechar?". Sai do ar
 * assim que o aviso é enviado e só volta quando uma nova navegação abre outra
 * página — o aviso de entrada continua saindo do middleware do Next, que roda a
 * cada mudança de rota.
 */
export function usePageExitBeacon() {
  const pathname = usePathname()
  const noticeSentRef = useRef(false)

  useEffect(() => {
    // Login, convite e recuperação de senha não abrem página no cronômetro —
    // avisar saída de aba numa tela de login não faz sentido. A lista é a mesma
    // que o `accessedPagesMiddleware` usa para não registrar a entrada.
    if (isPublicPage(pathname)) return

    noticeSentRef.current = false

    const sendNotice = () => {
      if (noticeSentRef.current) return
      noticeSentRef.current = true

      // Telemetria nunca vira erro visível para quem está saindo, e nunca
      // atrapalha a navegação: a promessa é solta e a falha morre aqui.
      authService.closePageView().catch(() => undefined)
    }

    window.addEventListener('pagehide', sendNotice)

    return () => {
      window.removeEventListener('pagehide', sendNotice)
    }
  }, [pathname])
}
